import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getAllSales,
  getMySales,
  toSaleApiError,
  type SaleHistoryQuery,
  type SaleHistoryScope,
  type SalePage,
} from '@/api/saleApi';

const DEFAULT_PAGE_SIZE = 20;
export const SALE_HISTORY_PAGE_SIZES = [10, 20, 50] as const;

const emptyPage = (pageSize: number): SalePage => ({
  currentPage: 1,
  totalPages: 0,
  pageSize,
  totalElements: 0,
  data: [],
});

const isSupportedPageSize = (
  pageSize: number,
): pageSize is typeof SALE_HISTORY_PAGE_SIZES[number] =>
  SALE_HISTORY_PAGE_SIZES.includes(pageSize as typeof SALE_HISTORY_PAGE_SIZES[number]);

export const useSaleHistory = (scope: SaleHistoryScope) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageData, setPageData] = useState<SalePage>(() => emptyPage(DEFAULT_PAGE_SIZE));
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const requestId = useRef(0);

  const query = useMemo<SaleHistoryQuery>(() => {
    const requestedPage = Number(searchParams.get('page'));
    const requestedSize = Number(searchParams.get('size'));
    return {
      page: Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1,
      size: Number.isInteger(requestedSize) && isSupportedPageSize(requestedSize)
        ? requestedSize
        : DEFAULT_PAGE_SIZE,
    };
  }, [searchParams]);

  const loadSales = useCallback(async () => {
    const activeRequest = ++requestId.current;
    setIsLoading(true);
    setLoadError('');
    try {
      const response = scope === 'all'
        ? await getAllSales(query)
        : await getMySales(query);
      if (activeRequest === requestId.current) {
        setPageData(response);
        setHasLoaded(true);
      }
    } catch (error: unknown) {
      if (activeRequest === requestId.current) {
        setLoadError(toSaleApiError(error).message);
        setHasLoaded(true);
      }
    } finally {
      if (activeRequest === requestId.current) setIsLoading(false);
    }
  }, [query, scope]);

  useEffect(() => {
    void loadSales();
    return () => {
      requestId.current += 1;
    };
  }, [loadSales]);

  const updateQuery = useCallback((updates: Record<string, string | undefined>) => {
    const nextParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) nextParams.set(key, value);
      else nextParams.delete(key);
    });
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const setPage = useCallback((page: number) => {
    updateQuery({ page: page > 1 ? String(page) : undefined });
  }, [updateQuery]);

  const setPageSize = useCallback((pageSize: number) => {
    updateQuery({
      size: pageSize === DEFAULT_PAGE_SIZE ? undefined : String(pageSize),
      page: undefined,
    });
  }, [updateQuery]);

  return {
    query,
    pageData,
    isInitialLoading: !hasLoaded && isLoading,
    isTableLoading: isLoading,
    loadError,
    setPage,
    setPageSize,
    refresh: loadSales,
    retry: loadSales,
  };
};
